<script lang="ts">
	/**
	 * `/` — the storefront home (Design §14). Replaces the template's dashboard.
	 *
	 * **Public, and that is the constraint everything here is built around.** It renders with no
	 * token, which is the whole point of the anonymous-browsing rework in `AbstractID.ts`, so the
	 * catalogue half goes through `optionalAuthFetch` and never `authenticatedFetch` — which
	 * throws without a token.
	 *
	 * The account half is the exception that proves it: `getAccountSummary` **is** an
	 * `authenticatedFetch`, so it runs only when `isAuthorized` is true and its failure is
	 * swallowed. A signed-out visitor never reaches it, and a signed-in one whose summary will not
	 * load still gets the shop — the store's front page failing because an *account* read failed
	 * would be a customer unable to buy because of a panel about what they already own.
	 *
	 * **The catalogue is shown to everyone**, subscribers included, below their summary. Hiding it
	 * from existing customers would be a storefront that stops selling to the people most likely to
	 * buy again.
	 */

	import { onMount } from 'svelte';
	import { Button } from 'flowbite-svelte';
	import {
		CurrencySelector,
		EmptyState,
		PageHeader,
		PriceDisplay,
		RoutedButton,
		RoutedLink,
		SubscriptionStatusBadge,
		TierLadder,
		CatalogueSkeleton,
		SkeletonWrapper
	} from '../../components';
	import { getAccountSummary, listCatalogProducts, listFamilies } from '../../AppNetwork';
	import { configurationStore } from '../../AppConfiguration';
	import { isAuthorized } from '../../AbstractID';
	import {
		activeCurrency,
		activeCurrencyCode,
		availableCurrencies,
		pinnedCurrency,
		selectCurrency,
		subscriptionPriceLine
	} from '../../stores/storefront';
	import { accessEndsAt, formatCalendarDate, mediaURL } from '../../Utilities';
	import { track } from '../../stores/analytics';
	import type { AccountSubscription, Price, Product, ProductFamily } from '../../AppTypes';
	import site from '../../site';

	let families: ProductFamily[] = [];
	/**
	 * Products in no family. The store is the whole catalogue now — there was a `/Products` page
	 * beside it that was the only screen listing these, and folding it in without them would take
	 * them off sale with nothing saying so.
	 */
	let standalone: Product[] = [];
	let loading: boolean = true;
	let loadError: string = '';

	/** What the caller already pays for. Empty for a signed-out visitor, and for a new customer. */
	let subscriptions: AccountSubscription[] = [];

	$: isOpen = $configurationStore?.isOpen ?? false;

	/**
	 * The account summary, for a signed-in caller only. See the note at the top: swallowed on
	 * failure, and cleared when signed out so a sign-out cannot leave the previous customer's
	 * subscriptions on the screen.
	 */
	async function fnLoadAccount(signedIn: boolean): Promise<void> {
		if (!signedIn) {
			subscriptions = [];
			return;
		}
		try {
			subscriptions = (await getAccountSummary()).subscriptions;
		} catch {
			subscriptions = [];
		}
	}

	$: void fnLoadAccount($isAuthorized);

	/** Live enough to be worth summarising — and to be worth offering an upgrade from. */
	const LIVE_STATUSES = ['active', 'trialing', 'past_due'];
	$: activeSubscriptions = subscriptions.filter(entry => LIVE_STATUSES.includes(entry.status));

	function headline(product: Product): Price | null {
		return product.prices.find(price => price.isDefault) ?? product.prices[0] ?? null;
	}

	/**
	 * The tiers this customer could move up to, and nothing else.
	 *
	 * Read off the server's own `action` discriminator, which the catalogue computes for the
	 * authenticated caller: `upgrade` means §9.3's rank rule says this is a move up from something
	 * they hold. Deriving it here from ranks and subscriptions would put that rule in a second
	 * place and eventually offer an upgrade the API refuses — the exact failure
	 * `PlanComparisonTable` is written to avoid.
	 *
	 * A tier blocked by a scheduled change answers `unavailable` and drops out on its own: an
	 * upsell the customer cannot act on is worse than no upsell.
	 */
	$: upsells = families
		.flatMap(family => family.products.map(product => ({ family, product, price: headline(product) })))
		.filter(entry => entry.price !== null && entry.price.action === 'upgrade')
		.sort((left, right) => left.product.rank - right.product.rank);

	async function fnLoad(currencyCode: string): Promise<void> {
		loading = true;
		loadError = '';
		try {
			// Together and guarded together: both are what is for sale, and a page showing the lines
			// while silently missing the individual products reads as a complete catalogue.
			const [familyResult, productResult] = await Promise.all([
				listFamilies({ currency: currencyCode || undefined }),
				listCatalogProducts({ currency: currencyCode || undefined, limit: 200 })
			]);
			families = familyResult.items;
			standalone = productResult.items.filter(product => product.familySlug === null);
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	// Re-read whenever the currency changes: the amounts and the `unavailable` flags are computed
	// per currency server-side, so a client-side re-render would show stale figures.
	$: void fnLoad($activeCurrencyCode);

	/* §12.2's first funnel stage. The landing page is a catalogue view like any other. */
	onMount(() => track('catalog_view', { path: '/' }));
</script>

<PageHeader>
	<svelte:fragment slot="header">
		<span class="text-gradient-primary">{$configurationStore?.name ?? site.name}</span>
	</svelte:fragment>
	<svelte:fragment slot="description">
		{#if activeSubscriptions.length > 0}
			<!-- A returning customer is not browsing for the first time, and greeting them as
			     though they were is the smallest way a store forgets who it is talking to. -->
			What you have, and where you could go next.
		{:else}
			Licences and subscriptions for the Abstract product line. Browse everything below. You
			only need an account when you buy.
		{/if}
	</svelte:fragment>
</PageHeader>

<!--
	What the customer already pays for, before what they could buy.
	
	A storefront that opens on the catalogue for somebody who already subscribes makes them navigate
	to find the thing they came for — and the two questions a subscriber arrives with are "what am I
	on" and "when am I next charged".
-->
{#if activeSubscriptions.length > 0}
	<section class="mb-10">
		<div class="flex flex-wrap items-baseline gap-3 mb-4">
			<h2 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Your subscriptions</h2>
			<RoutedLink to="/Account" class="text-sm text-primary-500 hover:underline">
				Manage them all<i class="fa-solid fa-arrow-right ml-1 text-xs" />
			</RoutedLink>
		</div>

		<div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
			{#each activeSubscriptions as entry (entry.uuid)}
				<!--
					The whole card, as a real anchor — the same gesture as `/Account` and
					`/Account/Subscriptions`, so a subscription card behaves the same wherever it is
					shown. Nothing inside may be a link or a button: nested interactive elements are
					invalid HTML and the inner one steals the click.
				-->
				<RoutedLink
					to={`/Account/Subscription/${entry.uuid}`}
					class="widget bg-depth border-widget bg-depth-hover flex flex-col gap-2 no-underline text-inherit"
				>
					<div class="flex flex-wrap items-center gap-2">
						<span class="font-bold text-lg">{entry.productName}</span>
						<!-- Status in words, never colour alone: `past_due` must not read as "broken". -->
						<SubscriptionStatusBadge forCustomer status={entry.status} cancelAtPeriodEnd={entry.cancelAtPeriodEnd} />
					</div>

					<!-- Through the shared helper, so this page, the subscriptions list and the account
					     summary cannot drift about how a per-seat plan is described (§9.7). -->
					<div class="text-sm">{$subscriptionPriceLine(entry)}</div>

					<p class="text-xs text-default">
						{#if accessEndsAt(entry) !== null}
							<!-- The same date means the opposite thing once a cancellation is scheduled. -->
							<i class="fa-solid fa-circle-stop mr-1" />Ends {formatCalendarDate(accessEndsAt(entry) ?? '')}
						{:else if entry.trialEnd !== null}
							<i class="fa-solid fa-gift mr-1" />Trial ends {formatCalendarDate(entry.trialEnd)}
						{:else if entry.currentPeriodEnd !== null}
							<i class="fa-solid fa-calendar-day mr-1" />Renews {formatCalendarDate(entry.currentPeriodEnd)}
						{/if}
					</p>

					<div class="grow" />
					<!-- The affordance the Manage button carried: the row has to look like a way in. -->
					<span class="text-sm text-primary-500 font-medium">
						Manage<i class="fa-solid fa-chevron-right ml-1.5 text-xs" aria-hidden="true" />
					</span>
				</RoutedLink>
			{/each}
		</div>
	</section>

	{#if upsells.length > 0 && isOpen}
		<!--
			The upsell, and only where the server says a move up is actually available. Every tier
			here answers `upgrade`, which is §9.3's rank rule reporting that this is a step up from
			something the customer holds — not this page's opinion of one.
		-->
		<section class="mb-10">
			<div class="flex flex-wrap items-baseline gap-3 mb-4">
				<h2 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Ready for more?</h2>
				<span class="text-sm text-default">
					Move up whenever you like, and you keep what you have already paid for.
				</span>
			</div>

			<div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
				{#each upsells as entry (entry.product.uuid)}
					<div class="widget bg-depth widget-spotlight flex flex-col gap-3">
						<div class="flex items-start gap-3">
							{#if entry.product.imageURL}
								<img
									src={mediaURL(entry.product.imageURL)}
									alt=""
									class="w-11 h-11 rounded-xl object-contain shrink-0"
									loading="lazy"
								/>
							{/if}
							<div class="min-w-0">
								<div class="font-bold">{entry.product.name}</div>
								<div class="text-xs text-default">in {entry.family.name}</div>
							</div>
						</div>

						{#if entry.product.tagline}
							<p class="text-sm text-default">{entry.product.tagline}</p>
						{/if}

						<PriceDisplay
							price={entry.price}
							currency={$activeCurrency}
							currencyCode={entry.product.currency}
							class="tabular-nums"
						/>

						<div class="grow" />
						<!-- To the product page, which is the one place a change is quoted and applied. -->
						<RoutedButton href={`/Product/${entry.product.slug}`} size="sm" color="primary">
							See what it adds
						</RoutedButton>
					</div>
				{/each}
			</div>
		</section>
	{/if}
{/if}

{#if !isOpen}
	<div class="widget bg-depth border-widget mb-6">
		<div class="flex items-start gap-3">
			<i class="fa-solid fa-circle-info text-primary-500 mt-1" />
			<div>
				<p class="font-semibold">The store is not open yet.</p>
				<p class="text-sm text-default">
					You can browse the catalogue, but purchases are not being taken at the moment.
				</p>
			</div>
		</div>
	</div>
{/if}

<!-- The currency the whole catalogue below is priced in. It lived on the Catalogue page, which this
     page replaced, and a storefront that cannot be re-priced is one that only ever sells in the
     currency it guessed. -->
<div class="controlpanel flex flex-wrap items-center gap-4 mb-8">
	<CurrencySelector
		currencies={$availableCurrencies}
		value={$activeCurrencyCode}
		pinnedCurrency={$pinnedCurrency}
		compact
		on:change={event => selectCurrency(event.detail)}
	/>
</div>

{#if loading}
	<!-- The page's own shape — mastheads over ladders — so the catalogue fills in rather than jumps in. -->
	<SkeletonWrapper>
		<CatalogueSkeleton />
	</SkeletonWrapper>
{:else if loadError.length > 0}
	<EmptyState variant="error" title="The catalogue could not be loaded." description={loadError}>
		<Button color="alternative" size="sm" on:click={() => fnLoad($activeCurrencyCode)}>Try again</Button>
	</EmptyState>
{:else if families.length === 0 && standalone.length === 0}
	<EmptyState
		title="Nothing is on sale yet."
		description="The catalogue is empty. Check back soon."
	/>
{:else}
	<div class="space-y-12">
		{#each families as family (family.slug)}
			<section>
				<!--
					The family's masthead, then its ladder — the same pair `/Products/:familySlug`'s
					comparison opens with, so a line looks like a line wherever a customer meets it.
				-->
				<div class="flex flex-wrap items-start gap-4 mb-5">
					<!-- Only when there is one. An empty box beside every heading reads as a broken
					     image rather than as a line with no logo. -->
					{#if family.imageURL}
						<img
							src={mediaURL(family.imageURL)}
							alt=""
							class="w-14 h-14 rounded-2xl object-contain shrink-0"
							loading="lazy"
						/>
					{/if}
					<div class="min-w-0 grow">
						<h2 class="text-3xl font-bold tracking-tight text-gray-900 dark:text-white leading-none">
							{family.name}
						</h2>
						{#if family.description}
							<p class="text-default mt-2 max-w-3xl">{family.description}</p>
						{/if}
					</div>
					<div class="flex flex-col items-start sm:items-end gap-1 shrink-0">
						<span class="card-label">
							<i class="fa-solid fa-layer-group text-[11px]" />{family.products.length}
							{family.products.length === 1 ? 'tier' : 'tiers'}
						</span>
						{#if family.products.length > 1}
							<RoutedLink
								to={`/Products/${family.slug}`}
								class="text-sm font-medium text-primary-500 hover:underline"
							>
								Compare them side by side
								<i class="fa-solid fa-arrow-right ml-1 text-xs" />
							</RoutedLink>
						{/if}
					</div>
				</div>

				<TierLadder products={family.products} currency={$activeCurrency} />
			</section>
		{/each}

		{#if standalone.length > 0}
			<!--
				No family, so no ordering: drawn without the ascent and without the what-this-adds
				line, because both would claim a ladder that does not exist.
			-->
			<section>
				<div class="mb-5">
					<h2 class="text-3xl font-bold tracking-tight text-gray-900 dark:text-white leading-none">
						Individual products
					</h2>
					<p class="text-default mt-2 max-w-3xl">Sold on their own, with no tiers to move between.</p>
				</div>
				<TierLadder products={standalone} currency={$activeCurrency} isLadder={false} />
			</section>
		{/if}
	</div>
{/if}
